import { computed, signal, Signal, TemplateRef, WritableSignal } from '@angular/core';
import {
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventDeleteEvent,
  MbscEventDragEvent,
  MbscResource,
} from '@mobiscroll/angular';
import { MenuItem } from '@softapps/core';
import { MenuItem as LegacyMenuItem } from '@softline/application';
import { CalendarViewType } from '../types/calendar-view-type';
import { DepartmentHead } from '../types/department-head';
import { DetailEmployee } from '../types/employee';
import { RosterContent } from '../types/roster-content';
import { Structure } from '../types/structure';

export abstract class RosterService<
  TEvent extends RosterContent,
  TResource extends MbscResource = MbscResource,
> {
  menuItems: Signal<LegacyMenuItem[]> = signal<LegacyMenuItem[]>([]);
  abstract structure: Signal<Structure | undefined>;
  abstract resources: Signal<TResource[]>;

  calendarContentLoading: Signal<boolean> = signal(false);

  abstract events: Signal<TEvent[]>;

  calendarMenu: WritableSignal<TemplateRef<any> | undefined> = signal<
    TemplateRef<any> | undefined
  >(undefined);

  eventMenu: WritableSignal<TemplateRef<any> | undefined> = signal<
    TemplateRef<any> | undefined
  >(undefined);

  /** Set by the calendar component: closes the currently open context menu
      overlay. The component owns the overlay (it has the ViewContainerRef); the
      service triggers close from within handleContextMenuAction. */
  closeContextMenuOverlay: (() => void) | null = null;

  /** Which template the calendar opens on event / cell right-click. Overridable
      so a concrete roster can supply a custom menu (e.g. werkstatt's garage
      event menu). Defaults to the shared eventMenu / calendarMenu signals. */
  resolveEventMenu(): TemplateRef<any> | undefined {
    return this.eventMenu();
  }
  resolveCalendarMenu(): TemplateRef<any> | undefined {
    return this.calendarMenu();
  }

  abstract selectedEventIds: Signal<(string | number)[]>;

  selectedEvents = computed(() => {
    const events: TEvent[] = [];

    for (const eventId of this.selectedEventIds()) {
      const event = this.events().find((o) => o.id === eventId);
      if (!event) continue;
      events.push(event);
    }

    return events;
  });

  abstract employeeDetail: Signal<Map<number | string, DetailEmployee> | null>;

  onMenuItemExecute<T>(_: MenuItem<T>): void {
    // default implementation does nothing...
  }

  // Functions for Calendar behavior
  abstract onEventCreate(e: MbscEventCreateEvent, inst: any): Promise<void>;

  abstract onEventClick(args: MbscEventClickEvent, inst: any): Promise<void>;

  abstract onEventRightClick(args: MbscEventClickEvent, inst: any): Promise<void>;

  abstract onEventDoubleClick(args: MbscEventClickEvent, inst: any): Promise<void>;

  abstract onCellClick(args: MbscCellClickEvent, inst: any): Promise<void>;

  abstract onCellRightClick(args: MbscCellClickEvent, inst: any): Promise<void>;

  abstract onCellDoubleClick(args: MbscCellClickEvent, inst: any): Promise<void>;

  abstract onEventDragStart(args: MbscEventDragEvent, inst: any): Promise<void>;

  abstract onEventDragEnd(args: MbscEventDragEvent, inst: any): Promise<void>;

  abstract onEventDelete(args: MbscEventDeleteEvent, inst: any): boolean;

  abstract deleteEvents(events: TEvent[]): Promise<boolean>;

  abstract deleteSelectedEvents(): Promise<boolean> | Promise<void>;

  abstract onKeyDown(event: KeyboardEvent): Promise<void>;

  abstract registerDepartmentHead(value: Signal<DepartmentHead | null>): void;

  abstract registerBeginDate(value: Signal<string>): void;

  abstract registerCalenderViewType(value: Signal<CalendarViewType>): void;

  abstract registerPlanMonthRange(from: Signal<Date>, to: Signal<Date>): void;

  abstract registerCalendarColumnWidth(value: Signal<string>): void;

  abstract registerCalendarDetails(value: Signal<'compact' | 'all'>): void;

  abstract registerEventTypesFilter(value: Signal<string[]>): void;

  abstract registerResourceFilterString(value: Signal<string>): void;

  abstract handleContextMenuAction(action: string): void;

  abstract calendarDetails;

  abstract loadEmployeeContent(structure: Structure, date: string): Promise<void>;

  abstract startOfPeriod;

  abstract endOfPeriod;

  copyEvents;

  abstract refreshEvents(): void;
}
